/**
 * Auto-advancing product carousel.
 *
 * The grid is already a scroll-snapping horizontal rail on mobile; carousel mode
 * keeps that rail at every width and advances it on a timer. Scrolling the real
 * container rather than transforming a track means keyboard focus, touch
 * momentum, scroll-snap and the scrollbar all keep working for free, and the
 * no-JS case degrades to a plain swipeable rail rather than a frozen one.
 *
 * The timer stops whenever advancing would be an interruption rather than an
 * animation:
 *   - the shopper is hovering or has focus inside it (they are reading a card)
 *   - they tap, swipe, wheel or arrow the rail themselves, in which case it
 *     stays out of the way for a few seconds before picking back up
 *   - the tab is hidden (otherwise it advances 300 times in a background tab)
 *   - prefers-reduced-motion is set, in which case it never starts at all
 */

(function () {
  const SELECTOR = '[data-alpha-carousel]';

  class AlphaProductsCarousel {
    constructor(root) {
      this.root = root;
      this.rail = root.querySelector('.alpha-products__grid');
      if (!this.rail) return;

      const seconds = parseFloat(root.dataset.autoplay || '0');
      if (!seconds) return;

      this.interval = seconds * 1000;
      this.timer = null;
      this.paused = false;
      this.resumeTimer = null;

      // Motion for its own sake is exactly what this setting suppresses. The rail
      // stays swipeable; it just never moves on its own.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.prev = root.querySelector('[data-alpha-carousel-prev]');
      this.next = root.querySelector('[data-alpha-carousel-next]');

      this.cloneForLoop();
      this.bind();
      this.bindArrows();
      this.updateArrows();
      this.start();
    }

    bind() {
      // TEMPORARY pause: the shopper is reading, but has not taken over. Hovering
      // out hands control back to the timer.
      const pause = () => { this.paused = true; };
      const resume = () => { this.paused = false; };

      /*
       * Hover pause is bound to the RAIL, not the whole section.
       *
       * On the root it covered the heading, the "view all" link and the arrows -
       * so simply having the pointer anywhere near the row stopped it, and it
       * read as "autoplay does not work" to anyone watching it with a mouse on
       * screen. Pausing belongs to the cards themselves.
       */
      this.rail.addEventListener('mouseenter', pause);
      this.rail.addEventListener('mouseleave', resume);
      this.root.addEventListener('focusin', pause);
      this.root.addEventListener('focusout', resume);

      /*
       * Driving it by hand pauses it too, then it picks back up once they stop.
       *
       * The delay matters more than the pause. Resuming the instant a swipe ends
       * would yank the row out from under the finger about to tap a card, so the
       * timer stays out of the way for a few seconds of quiet first. Touch has no
       * mouseleave to resume from, which is the other reason this is time-based
       * rather than event-based.
       */
      const nudged = () => this.pauseBriefly();

      this.rail.addEventListener('pointerdown', nudged);
      this.rail.addEventListener('touchstart', nudged, { passive: true });

      /*
       * HORIZONTAL wheel only.
       *
       * Listening to every wheel event meant that scrolling the PAGE with the
       * cursor anywhere over the row paused it for six seconds - so it appeared
       * to stop on its own, without the shopper ever touching it. A vertical
       * wheel is someone scrolling past this section, not driving it.
       */
      this.rail.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) nudged();
        },
        { passive: true }
      );
      this.rail.addEventListener('keydown', (event) => {
        // Only navigation keys count. Tabbing through cards is not driving it.
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
          nudged();
        }
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      });
    }

    /*
     * Seamless loop.
     *
     * Jumping scrollLeft back to 0 at the end is visible as a hard snap, however
     * you ease it - the whole row flies backwards past several products.
     *
     * Instead the card list is duplicated once. When the rail scrolls past the
     * width of the FIRST set, scrollLeft is silently reduced by exactly that
     * width. The pixels either side of that moment are identical, because the
     * shopper is looking at clone N of the same product, so the rewind is
     * invisible and the row appears to run forever.
     *
     * The clones are inert: aria-hidden keeps them out of the accessibility
     * tree, ids are stripped so quick-add forms cannot collide with the
     * originals, and every focusable inside is removed from the tab order.
     */
    cloneForLoop() {
      const originals = [...this.rail.children];
      if (originals.length < 2) return;

      this.originalCount = originals.length;

      originals.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-carousel-clone', '');
        // Duplicate ids break every label/aria reference on the page, and Dawn's
        // quick-add binds by id.
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.rail.appendChild(clone);
      });

      this.looped = true;
    }

    // Width of one full set of cards, measured live so it survives a resize.
    setWidth() {
      if (!this.looped) return 0;
      const styles = window.getComputedStyle(this.rail);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      let total = 0;
      for (let i = 0; i < this.originalCount; i += 1) {
        const child = this.rail.children[i];
        if (child) total += child.getBoundingClientRect().width + gap;
      }
      return total;
    }

    // Called after any scroll. Rewinding by exactly one set keeps the visible
    // content identical across the seam.
    normalise() {
      if (!this.looped) return;
      const width = this.setWidth();
      if (width > 0 && this.rail.scrollLeft >= width) {
        this.rail.scrollLeft = this.rail.scrollLeft - width;
      }
    }

    bindArrows() {
      const step = () => {
        const first = this.rail.firstElementChild;
        if (!first) return 0;
        const styles = window.getComputedStyle(this.rail);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      if (this.prev) {
        this.prev.addEventListener('click', () => {
          this.pauseBriefly();
          this.rail.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }

      if (this.next) {
        this.next.addEventListener('click', () => {
          this.pauseBriefly();
          this.rail.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      // Disabled state tracks the real scroll position, so it stays correct
      // whether the rail was moved by an arrow, a swipe or the timer.
      let queued = false;
      this.rail.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          this.normalise();
          this.updateArrows();
        });
      }, { passive: true });

      window.addEventListener('resize', () => this.updateArrows(), { passive: true });
    }

    updateArrows() {
      const rail = this.rail;
      const overflow = rail.scrollWidth - rail.clientWidth;
      const atStart = rail.scrollLeft <= 8;
      // A looping rail has no start or end to run out of.
      if (this.prev) this.prev.disabled = this.looped ? false : atStart;
      // Never disable "next": the timer loops back to the start from the end, so
      // the control should be able to do the same thing the autoplay does.
      if (this.next) this.next.disabled = overflow < 8;
      this.root.toggleAttribute('data-carousel-static', overflow < 8);
    }

    pauseBriefly() {
      this.paused = true;
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        // Do not override a hover/focus pause that is still genuinely active.
        if (!this.rail.matches(':hover') && !this.root.contains(document.activeElement)) {
          this.paused = false;
        }
      }, 6000);
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.advance(), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      if (this.paused) return;

      const rail = this.rail;
      // Nothing to scroll: fewer cards than fit. Advancing would jitter the rail
      // against its own end stop once a second.
      const overflow = rail.scrollWidth - rail.clientWidth;
      if (overflow < 8) return;

      const first = rail.firstElementChild;
      if (!first) return;

      // Step by one card plus the gap, so a card always lands flush rather than
      // stopping halfway. Measured live because the column width is a percentage
      // that changes with the viewport.
      const styles = window.getComputedStyle(rail);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const step = first.getBoundingClientRect().width + gap;

      // With the loop in place there is no "end" to handle: normalise() rewinds
      // one set at a time behind the scenes, so this only ever moves forwards.
      if (!this.looped) {
        const atEnd = rail.scrollLeft >= overflow - 8;
        if (atEnd) {
          rail.scrollTo({ left: 0, behavior: 'auto' });
          this.updateArrows();
          return;
        }
      }

      rail.scrollBy({ left: step, behavior: 'smooth' });
    }
  }

  const init = function (root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      if (el.dataset.alphaCarouselReady) return;
      el.dataset.alphaCarouselReady = 'true';
      new AlphaProductsCarousel(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section into a fresh DOM node.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
